<script setup>
import { ref, computed } from 'vue'
import ChartView from '@/components/charts/ChartView.vue'
import { store } from '@/store/appStore.js'

const pob = computed(() => store.salesSupervisorV2.pob.value)
const timeframe = ref('Monthly')
const windowData = computed(() => pob.value[timeframe.value.toLowerCase()])

const trendChart = computed(() => ({
  labels: pob.value.trend.map(m => m.month.slice(5)),
  datasets: [{ label: 'Achieved (USD)', data: pob.value.trend.map(m => m.value), borderColor: '#0F3D3E', backgroundColor: 'rgba(15,61,62,0.08)', tension: 0.3, fill: true, pointRadius: 3 }]
}))
const territoryChart = computed(() => ({
  labels: pob.value.territoryWise.map(t => t.territory),
  datasets: [{ label: 'Value (USD)', data: pob.value.territoryWise.map(t => t.value), backgroundColor: '#C99A2E' }]
}))
const productChart = computed(() => ({
  labels: pob.value.productWise.slice(0, 8).map(p => p.productName),
  datasets: [{ label: 'Value (USD)', data: pob.value.productWise.slice(0, 8).map(p => p.value), backgroundColor: '#0F3D3E' }]
}))
</script>

<template>
  <div>
    <div class="card module-header"><h2>Team POB Dashboard</h2></div>

    <div class="card" style="padding:16px 20px;margin-bottom:16px;">
      <div style="display:flex;gap:4px;margin-bottom:12px;">
        <button v-for="tf in ['Daily','Monthly','Quarterly','Annual']" :key="tf" class="filter-select"
                :style="timeframe===tf?'background:var(--color-primary-dark);color:#fff;border-color:var(--color-primary-dark);':'cursor:pointer;'"
                @click="timeframe=tf">{{ tf }}</button>
      </div>
      <div class="stat-row" style="margin-bottom:0;">
        <div class="card stat-card"><div class="stat-value">${{ windowData.target.toLocaleString() }}</div><div class="stat-label">Team Target</div></div>
        <div class="card stat-card"><div class="stat-value">${{ windowData.achieved.toLocaleString() }}</div><div class="stat-label">Team Achieved</div></div>
        <div class="card stat-card"><div class="stat-value" :style="pob.achievementPct>=100?'color:var(--status-green);':'color:var(--status-red);'">{{ pob.achievementPct }}%</div><div class="stat-label">Team Achievement %</div></div>
      </div>
    </div>

    <div class="card" style="margin-bottom:16px;">
      <div class="toolbar"><span class="result-count">Per Individual</span></div>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>Sales Exec</th><th>Target</th><th>Achieved</th><th>Achievement %</th></tr></thead>
          <tbody>
            <tr v-for="e in pob.byIndividual" :key="e.execName">
              <td class="cell-strong">{{ e.execName }}</td><td class="cell-muted">${{ e.target.toLocaleString() }}</td>
              <td class="cell-muted">${{ e.achieved.toLocaleString() }}</td>
              <td :style="e.achievementPct>=100?'color:var(--status-green);font-weight:700;':e.achievementPct<70?'color:var(--status-red);font-weight:700;':''">{{ e.achievementPct }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="split-2col" style="margin-bottom:16px;">
      <div class="card" style="padding:18px 20px;">
        <div class="eyebrow" style="margin-bottom:8px;">Trend</div>
        <ChartView type="line" :labels="trendChart.labels" :datasets="trendChart.datasets" :height="220" key="ss-pob-trend" />
      </div>
      <div class="card" style="padding:18px 20px;">
        <div class="eyebrow" style="margin-bottom:8px;">Territory Performance</div>
        <ChartView type="bar" :labels="territoryChart.labels" :datasets="territoryChart.datasets" :height="220" key="ss-pob-territory" />
      </div>
    </div>

    <div class="card" style="padding:18px 20px;margin-bottom:16px;">
      <div class="eyebrow" style="margin-bottom:8px;">Product Performance</div>
      <ChartView type="bar" :labels="productChart.labels" :datasets="productChart.datasets" :height="220" key="ss-pob-product" />
    </div>

    <div class="card">
      <div class="toolbar"><span class="result-count">Pharmacy Ranking</span></div>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>#</th><th>Pharmacy</th><th>Territory</th><th>Value</th><th>Orders</th></tr></thead>
          <tbody>
            <tr v-for="(p,i) in pob.pharmacyWise" :key="p.customerId">
              <td class="cell-muted">{{ i+1 }}</td><td class="cell-strong">{{ p.pharmacyName }}</td>
              <td class="cell-muted">{{ p.territory || '—' }}</td>
              <td class="cell-muted">${{ p.value.toLocaleString() }}</td><td class="cell-muted">{{ p.orderCount }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
